import { useEffect } from "react";

const setMeta = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) tag.setAttribute("content", content);
};

const setCanonical = (href) => {
  const tag = document.querySelector('link[rel="canonical"]');
  if (tag) tag.setAttribute("href", href);
};

export const usePageMeta = (title, description, path) => {
  useEffect(() => {
    document.title = title;
    setMeta("description", description);
    setCanonical(`https://www.cogniticfo.com${path}`);
  }, [title, description, path]);
};

const H = ({ children }) => (
  <h2 className="mt-12 font-display text-2xl font-bold tracking-tight text-navy">{children}</h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-base leading-relaxed text-body">{children}</p>
);

const UL = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-body">
        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const LegalLayout = ({ testId, title, effectiveDate, children }) => (
  <main data-testid={testId} className="bg-white pt-[72px]">
    <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
      <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-brand-hover">
        <span className="h-px w-8 bg-brand" aria-hidden="true" />
        Legal
      </p>
      <h1 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight text-navy">{title}</h1>
      <p className="mt-4 text-sm font-semibold text-navy/50">Effective Date: {effectiveDate}</p>
      <div className="mt-10 border-t border-border pt-2">{children}</div>
    </div>
  </main>
);

export const PrivacyPolicy = () => {
  usePageMeta(
    "Privacy Policy | Cogniti.ai CFO",
    "Privacy Policy for Cogniti.ai CFO, LLP and www.cogniticfo.com.",
    "/privacy-policy"
  );
  return (
    <LegalLayout testId="privacy-policy-page" title="Privacy Policy" effectiveDate="16 August 2026">
      <P>
        Cogniti.ai CFO, LLP (“Cogniti.ai CFO”, “we”, “us” or “our”) respects your
        privacy and is committed to handling personal information responsibly.
      </P>
      <P>
        This Privacy Policy explains how we collect, use and protect information
        when you visit www.cogniticfo.com, contact us, or submit an enquiry
        through the website.
      </P>

      <H>1. Information we may collect</H>
      <P>We may collect information that you voluntarily provide to us, including:</P>
      <UL items={[
        "name;",
        "company or organisation name;",
        "email address;",
        "telephone number;",
        "job title or role;",
        "information about your business or finance requirements;",
        "messages or information submitted through contact or consultation forms; and",
        "any other information you choose to provide to us.",
      ]} />
      <P>
        We may also collect limited technical information automatically when you
        use the website, such as browser type, device type, IP address, pages
        visited, referral source and general website usage information, where such
        information is made available through our hosting or analytics tools.
      </P>

      <H>2. How we use your information</H>
      <P>We may use the information collected to:</P>
      <UL items={[
        "respond to enquiries and consultation requests;",
        "understand your requirements and communicate with you about our services;",
        "provide proposals or information requested by you;",
        "manage prospective and existing client relationships;",
        "improve the website, services and user experience;",
        "maintain the security and proper functioning of the website;",
        "comply with applicable legal, regulatory or professional obligations; and",
        "maintain appropriate business records.",
      ]} />
      <P>
        We will not use personal information for purposes unrelated to those for
        which it was collected unless permitted by law or with appropriate consent.
      </P>

      <H>3. Sharing of information</H>
      <P>We do not sell personal information.</P>
      <P>
        We may share information with service providers that support our website
        or business operations, such as website hosting, email, analytics, CRM,
        scheduling or cloud-service providers, where reasonably necessary for the
        purposes described above.
      </P>
      <P>
        We may also disclose information where required by law, regulation, legal
        process or a competent authority.
      </P>
      <P>
        Where service providers process information on our behalf, we expect them
        to use it only for the relevant service and to apply appropriate safeguards.
      </P>

      <H>4. International processing</H>
      <P>
        Some of our technology or service providers may process or store
        information outside India.
      </P>
      <P>
        Where personal information is transferred or processed internationally, we
        will seek to use appropriate service providers and safeguards and comply
        with applicable legal requirements governing such transfers.
      </P>

      <H>5. Data retention</H>
      <P>
        We retain personal information only for as long as reasonably necessary
        for the purpose for which it was collected, for legitimate business
        purposes, or as required by applicable law, regulatory requirements or
        professional obligations.
      </P>
      <P>
        Enquiries that do not result in an engagement may be deleted or anonymised
        after a reasonable period unless there is a legitimate reason to retain them.
      </P>

      <H>6. Security</H>
      <P>
        We take reasonable technical and organisational measures to protect
        personal information against unauthorised access, loss, misuse, alteration
        or disclosure.
      </P>
      <P>
        However, no internet transmission or electronic storage system can be
        guaranteed to be completely secure.
      </P>

      <H>7. Cookies and analytics</H>
      <P>
        The website may use essential cookies and limited analytics or similar
        technologies to understand website usage and improve performance.
      </P>
      <P>
        If we introduce non-essential advertising, behavioural tracking or
        marketing cookies in the future, this policy and any required consent
        mechanism will be updated accordingly.
      </P>

      <H>8. Your choices and rights</H>
      <P>Subject to applicable law, you may contact us to:</P>
      <UL items={[
        "ask what personal information we hold about you;",
        "request correction or updating of inaccurate information;",
        "request deletion of information where appropriate;",
        "withdraw consent where processing is based on consent; or",
        "raise a concern regarding our handling of your personal information.",
      ]} />
      <P>
        Certain information may need to be retained where required by law or for
        legitimate legal, regulatory or contractual purposes.
      </P>

      <H>9. Third-party links</H>
      <P>
        Our website may contain links to third-party websites, including LinkedIn
        or other external services.
      </P>
      <P>
        We are not responsible for the privacy practices, security or content of
        third-party websites. You should review their respective privacy policies
        before providing personal information.
      </P>

      <H>10. Changes to this Privacy Policy</H>
      <P>
        We may update this Privacy Policy from time to time to reflect changes in
        our services, technology or legal requirements.
      </P>
      <P>
        The latest version will be available on this website with the revised
        effective date.
      </P>

      <H>11. Contact us</H>
      <P>For privacy-related questions, requests or concerns, please contact:</P>
      <P>
        <strong className="text-navy">Cogniti.ai CFO, LLP</strong><br />
        Email: <a href="mailto:ramesh@cogniticfo.com" data-testid="privacy-email-link" className="font-semibold text-brand-hover underline underline-offset-2">ramesh@cogniticfo.com</a><br />
        Website: www.cogniticfo.com
      </P>
    </LegalLayout>
  );
};

export const TermsOfService = () => {
  usePageMeta(
    "Terms of Service | Cogniti.ai CFO",
    "Terms governing use of the Cogniti.ai CFO website and its informational content.",
    "/terms-of-service"
  );
  return (
    <LegalLayout testId="terms-of-service-page" title="Terms of Service" effectiveDate="16 August 2026">
      <P>
        These Terms of Service (“Terms”) govern your use of www.cogniticfo.com,
        operated by Cogniti.ai CFO, LLP.
      </P>
      <P>
        By accessing or using this website, you agree to these Terms. If you do
        not agree with them, please do not use the website.
      </P>

      <H>1. Purpose of the website</H>
      <P>
        This website provides general information about Cogniti.ai CFO and its
        services, which may include:
      </P>
      <UL items={[
        "Fractional and Virtual CFO services;",
        "Finance Shared Services;",
        "Controllership and Reporting;",
        "FP&A and Investor Reporting;",
        "Compliance and Regulatory support;",
        "Treasury and Finance Transformation;",
        "finance operations and automation; and",
        "customised AI tools and workflow solutions for finance functions.",
      ]} />
      <P>
        Information on this website is provided for general informational and
        business-development purposes only.
      </P>

      <H>2. No professional engagement created</H>
      <P>
        Your use of this website, submission of an enquiry, or communication with
        us through the website does not by itself create a client, advisory,
        fiduciary, accountant-client or other professional relationship.
      </P>
      <P>
        A professional engagement begins only when the scope, terms and commercial
        arrangements have been separately agreed between Cogniti.ai CFO and the client.
      </P>

      <H>3. No accounting, tax, legal or investment advice</H>
      <P>
        Content on this website is general in nature and should not be treated as
        accounting, tax, legal, regulatory, investment or other professional
        advice applicable to any specific person or situation.
      </P>
      <P>
        You should obtain appropriate professional advice before making decisions
        based on particular facts or circumstances.
      </P>

      <H>4. Accuracy of information</H>
      <P>
        We seek to keep website information accurate and current, but we do not
        guarantee that all content will at all times be complete, accurate,
        current or free from errors.
      </P>
      <P>
        Laws, regulations, accounting requirements, technology and business
        practices may change over time.
      </P>
      <P>We may modify, update or remove website content without notice.</P>

      <H>5. Intellectual property</H>
      <P>
        Unless otherwise stated, the website design, text, branding, graphics,
        service descriptions and other original content are owned by or licensed
        to Cogniti.ai CFO, LLP.
      </P>
      <P>
        You may view and use the website for legitimate personal or
        business-information purposes.
      </P>
      <P>
        You may not copy, reproduce, republish, distribute, modify or commercially
        exploit substantial portions of the website without our prior written
        permission, except as permitted by law.
      </P>
      <P>
        Third-party names, trademarks and product names remain the property of
        their respective owners.
      </P>
      <P>
        References to technology platforms or products do not imply endorsement,
        partnership or affiliation unless expressly stated.
      </P>

      <H>6. AI-enabled tools and technology</H>
      <P>
        The website may describe AI-enabled finance processes, automation tools,
        accelerators or customised AI tools developed or configured by
        Cogniti.ai CFO.
      </P>
      <P>
        Such descriptions are illustrative of our capabilities and do not
        constitute a guarantee that any particular tool, functionality, result,
        saving or outcome will be suitable for every client.
      </P>
      <P>
        Actual solutions depend on client requirements, available data, technology
        environment, implementation scope and separately agreed engagement terms.
      </P>

      <H>7. Third-party websites and services</H>
      <P>
        The website may contain links to third-party websites or services.
      </P>
      <P>
        Such links are provided for convenience only. We do not control and are
        not responsible for third-party content, availability, privacy, security
        or practices.
      </P>

      <H>8. Limitation of liability</H>
      <P>
        To the extent permitted by applicable law, Cogniti.ai CFO, LLP and its
        partners, employees and representatives will not be liable for any loss or
        damage arising solely from reliance on general information contained on
        this website or from inability to access or use the website.
      </P>
      <P>Nothing in these Terms excludes any liability that cannot lawfully be excluded.</P>

      <H>9. Website availability and security</H>
      <P>We may modify, suspend or discontinue any part of the website without notice.</P>
      <P>
        Although reasonable measures are taken to maintain security, we do not
        warrant that the website will always be uninterrupted, error-free or free
        from viruses, malicious code or other harmful components.
      </P>

      <H>10. Privacy</H>
      <P>
        Use of personal information submitted through this website is governed by
        our{" "}
        <a href="/privacy-policy" data-testid="terms-privacy-link" className="font-semibold text-brand-hover underline underline-offset-2">
          Privacy Policy
        </a>.
      </P>

      <H>11. Governing law</H>
      <P>These Terms are governed by the laws of India.</P>
      <P>
        Any dispute relating to these Terms or use of the website will be subject
        to the jurisdiction of the competent courts in Hyderabad, Telangana,
        India, subject to any mandatory provisions of applicable law.
      </P>

      <H>12. Changes to these Terms</H>
      <P>We may update these Terms periodically.</P>
      <P>
        The revised Terms will take effect when published on the website, unless
        otherwise stated.
      </P>

      <H>13. Contact</H>
      <P>For questions regarding these Terms, please contact:</P>
      <P>
        <strong className="text-navy">Cogniti.ai CFO, LLP</strong><br />
        Email: <a href="mailto:ramesh@cogniticfo.com" data-testid="terms-email-link" className="font-semibold text-brand-hover underline underline-offset-2">ramesh@cogniticfo.com</a><br />
        Website: www.cogniticfo.com
      </P>
    </LegalLayout>
  );
};
